import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Slide, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';

const DISMISS_KEY = 'finextra-install-dismissed-at';
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

// iPadOS 13+ reports itself as Mac, so also check for touch support
const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

const isIOSChrome = () => /CriOS/i.test(window.navigator.userAgent);

const wasRecentlyDismissed = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
    return dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

// Android/desktop Chrome: shows an Install button using the beforeinstallprompt event.
// iOS (Safari and Chrome): there is no install event, so explain "Share → Add to Home Screen".
const InstallPWAButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(wasRecentlyDismissed);

  useEffect(() => {
    if (isStandalone()) return undefined;

    if (isIOS()) {
      setShowIOSHint(true);
      return undefined;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleInstalled = () => setDeferredPrompt(null);

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode); the banner just returns next visit
    }
  };

  const visible = !dismissed && (Boolean(deferredPrompt) || showIOSHint);

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          left: { xs: 12, sm: 'auto' },
          right: 12,
          bottom: 'calc(12px + env(safe-area-inset-bottom))',
          width: { sm: 380 },
          zIndex: 3000,
          p: 2,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          component="img"
          src="/icon-192x192.png"
          alt=""
          sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0 }}
        />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Install Mickles
          </Typography>
          {showIOSHint ? (
            <Typography variant="body2" color="text.secondary" component="div">
              Tap{' '}
              <IosShareIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', color: 'primary.main' }} />{' '}
              <strong>Share</strong>
              {isIOSChrome() ? ' (top right, in the address bar)' : ' (bottom toolbar)'}, then{' '}
              <AddBoxOutlinedIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom' }} />{' '}
              <strong>Add to Home Screen</strong>.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Add it to your home screen for quick, full-screen access.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleInstall}
                sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }}
              >
                Install App
              </Button>
            </>
          )}
        </Box>
        <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss install prompt" sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Slide>
  );
};

export default InstallPWAButton;
